import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import useSeo from "@/hooks/useSeo";
import { comics } from "@/mocks/comics";

export default function Detail() {
  const { id } = useParams();
  const comicId = Number(id);
  const comic = comics.find((c) => c.id === comicId) ?? comics[0];
  const related = comics.filter((c) => c.genre === comic.genre && c.id !== comic.id).slice(0, 5);

  const chapterList = Array.from({ length: Math.min(comic.chapters, 12) }, (_, i) => comic.chapters - i);

  useSeo({
    title: `${comic.title}漫画 - ${comic.genre}题材作品详情 | 羞羞动漫漫画`,
    description: `${comic.intro}${comic.title}是羞羞动漫漫画${comic.genre}题材人气作品，已更新${comic.chapters}话，评分${comic.score.toFixed(1)}，喜欢${comic.genre}漫画的你不要错过。`,
    keywords: `${comic.title},${comic.title}漫画,羞羞动漫漫画${comic.genre}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Book",
      name: comic.title,
      genre: comic.genre,
      inLanguage: "zh-CN",
      numberOfPages: comic.chapters,
      description: comic.intro,
      aggregateRating: { "@type": "AggregateRating", ratingValue: comic.score, bestRating: 10, worstRating: 1 },
    },
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="pt-24 md:pt-32 max-w-7xl mx-auto px-4 md:px-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="hover:text-primary-600 whitespace-nowrap">分类专区</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category?genre=${comic.genre}`} className="hover:text-primary-600 whitespace-nowrap">{comic.genre}漫画</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 line-clamp-1">{comic.title}</span>
          </nav>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-background-200">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} ${comic.genre}漫画`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  className="flex-1 whitespace-nowrap px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  开始阅读
                </button>
                <button
                  type="button"
                  className="flex-1 whitespace-nowrap px-4 py-3 rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors"
                >
                  <i className="ri-heart-line mr-2"></i>
                  收藏
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">{comic.genre}漫画</span>
                {comic.tags.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-sm">{t}</span>
                ))}
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-sm">{comic.status}</span>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 text-center">
                  <p className="text-xs text-foreground-500">评分</p>
                  <p className="mt-1 font-heading font-bold text-xl text-accent-600">
                    {comic.score.toFixed(1)}
                    <span className="text-xs text-foreground-400"> / 10</span>
                  </p>
                </div>
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 text-center">
                  <p className="text-xs text-foreground-500">人气</p>
                  <p className="mt-1 font-heading font-bold text-xl text-primary-600">{comic.views}</p>
                </div>
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 text-center">
                  <p className="text-xs text-foreground-500">收藏</p>
                  <p className="mt-1 font-heading font-bold text-xl text-foreground-900">{comic.likes.toLocaleString()}</p>
                </div>
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 text-center">
                  <p className="text-xs text-foreground-500">更新至</p>
                  <p className="mt-1 font-heading font-bold text-xl text-foreground-900">{comic.chapters} 话</p>
                </div>
              </div>

              <div className="mt-6">
                <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{comic.intro}</p>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  这部{comic.genre}题材漫画以细腻的情感刻画与轻松甜蜜的日常见长，剧情贴近生活又充满浪漫想象。无论你是初入恋爱后宫番的新手，还是资深追更党，都能在这里找到对味的故事。
                </p>
              </div>

              <div className="mt-6 flex items-center gap-6 text-sm text-foreground-600">
                <span className="flex items-center gap-1"><i className="ri-time-line text-primary-500"></i>最近更新：{comic.update}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line text-primary-500"></i>持续连载中</span>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">章节列表</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {chapterList.map((ch) => (
              <button
                key={ch}
                type="button"
                className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors"
              >
                <span className="line-clamp-1">第 {ch} 话</span>
                <i className="ri-play-circle-line text-primary-500"></i>
              </button>
            ))}
          </div>
          {comic.chapters > 12 && (
            <p className="mt-4 text-sm text-foreground-500">共 {comic.chapters} 话，更多章节持续更新中…</p>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">相关推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}